import { API_URLS } from '@chessx/shared/api-routes'
import React, { useEffect, useState, FormEvent, CSSProperties } from 'react'
import { Form } from 'react-bootstrap'
import './AdminUsersDetails.scss'
import { useLocation, useNavigate } from 'react-router-dom'
import useAdminStore from '../../store/AdminStore'
import axiosInstance from '../../network/axiosInstance'
import { toastErrorMixin, toastMixin } from '../../utils/interact'

interface UserDetail {
  _id: string
  userName?: string
  email?: string
  walletId?: string
  role?: string
  isActive?: boolean
  profilePic?: string
  [key: string]: unknown
}

interface UserProgress {
  gems?: number
  strength?: number
  coins?: number
  debugMode?: boolean
  world?: { world?: string }
  [key: string]: unknown
}

interface ArmyHeroRow {
  _id: string
  inUse?: boolean
  hero?: {
    _id?: string
    name?: string
    avatar?: string
    color?: string
    pieceType?: string
    rarity?: string
    heroPower?: string
  }
}

interface UserArmy {
  data?: ArmyHeroRow[]
}

interface FormFields {
  user?: Record<string, unknown>
  progress?: Record<string, unknown>
  army?: Record<string, unknown>
}

interface FormErrors {
  user?: Record<string, string | null>
  progress?: Record<string, string | null>
  army?: Record<string, string | null>
  [key: string]: unknown
}

interface UserDataState {
  userDetail?: UserDetail
  userProgress?: UserProgress
  userArmy?: UserArmy
}

export const PROGRESS_STAT_FIELDS = [
  { key: 'gems', label: 'Gems', color: '#f59e0b', type: 'number' },
  { key: 'strength', label: 'Strength', color: '#3b82f6', type: 'number' },
  { key: 'coins', label: 'Coins', color: '#f97316', type: 'number' }
] as const

const AdminUsersDetails = () => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [isEditMode, setEditMode] = useState(false)
  const [form, setForm] = useState<FormFields>({})
  const [errors, setErrors] = useState<FormErrors>({})
  const { fetchUserData, fetchUserProgress, fetchUserArmy, resetSeason } = useAdminStore()
  const [submitting, setSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userData, setUserData] = useState<UserDataState>({})

  const setField = (field: keyof FormFields, key: string, value: unknown) => {
    setForm({ ...form, [field]: { ...(form[field] || {}), [key]: value } })
    const fieldErrors = errors[field] as Record<string, string | null> | undefined
    if (fieldErrors?.[key]) {
      setErrors({ ...errors, [field]: { ...fieldErrors, [key]: null } })
    }
  }

  const getUserData = async (user: UserDetail | null | undefined) => {
    try {
      setIsLoading(true)
      const [userDetail, userProgress, userArmy] = await Promise.all([
        fetchUserData({ userId: user?._id }),
        fetchUserProgress({ userId: user?._id }),
        fetchUserArmy({ userId: user?._id })
      ])
      setUserData({
        userDetail: userDetail as UserDetail,
        userProgress: userProgress as UserProgress,
        userArmy: userArmy as UserArmy
      })
    } catch (err) {
      console.error('err', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getUserData(state as UserDetail)
  }, [state])

  const { userDetail, userProgress, userArmy } = userData

  const findFormErrors = (
    data: unknown,
    key: string,
    newErrors: Record<string, unknown>
  ): unknown => {
    if (['heroIds', 'world', 'role', 'debugMode'].includes(key)) return null
    if (typeof data === 'object' && data !== null) {
      const keys = Object.keys(data as Record<string, unknown>)
      if (keys.length) {
        keys.forEach(titleKey => {
          const err = findFormErrors(
            (data as Record<string, unknown>)[titleKey],
            titleKey,
            newErrors
          )
          if (err) newErrors = { ...newErrors, [titleKey]: err }
        })
      }
    } else {
      if (!data) return `${key.charAt(0).toUpperCase() + key.slice(1)} cannot be blank.`
      return null
    }
    if (newErrors && Object.keys(newErrors).length) return newErrors
    else return null
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement> | React.MouseEvent) => {
    e.preventDefault()
    const foundError = findFormErrors(form, 'error', {}) as Record<string, unknown> | null
    if (foundError && Object.keys(foundError).length) {
      setErrors(foundError as FormErrors)
      return
    }
    try {
      setSubmitting(true)
      if (form.user && userDetail) {
        await axiosInstance.put(API_URLS.ADMIN_UPDATE_USER, {
          ...form.user,
          userId: userDetail._id
        })
      }
      if (form.progress && userDetail) {
        const params: Record<string, unknown> = { ...form.progress, userId: userDetail._id }
        if (form.progress.strength) params.strength = form.progress.strength
        await axiosInstance.put(API_URLS.ADMIN_UPDATE_PROGRESS, params)
      }
      if (form.army?.heroIds && userDetail) {
        await axiosInstance.put(API_URLS.ADMIN_ADD_HEROES, {
          heroIds: form.army.heroIds,
          userId: userDetail._id
        })
      }
      toastMixin.fire({ title: 'User updated successfully.' })
    } catch {
      toastErrorMixin.fire({ title: 'Error updating data.' })
    } finally {
      getUserData(state as UserDetail)
      setSubmitting(false)
      setEditMode(false)
    }
  }

  const removeFromArmy = async (id: string, index: number) => {
    try {
      await axiosInstance.delete(API_URLS.ADMIN_DELETE_ARMY_HERO, {
        params: { id }
      })
      const newUser: UserDataState = { ...userData }
      newUser.userArmy?.data?.splice(index, 1)
      setUserData({ ...newUser })
    } catch {
      toastErrorMixin.fire({ title: 'Error removing Hero.' })
    }
  }

  if (isLoading) {
    return (
      <div className="detail-loading">
        <div className="detail-skeleton">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="skel-line"
              style={{ width: `${[80, 60, 90, 50, 70, 55][i]}%` }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (!userDetail) return null

  return (
    <div className="detail-page">
      {/* Top bar */}
      <div className="detail-topbar">
        <button className="detail-back-btn" onClick={() => navigate('/admin/users')}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Users
        </button>
        <div className="detail-topbar-actions">
          {isEditMode && (
            <button
              className="detail-cancel-btn"
              onClick={() => setEditMode(false)}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
          <button
            className={`detail-save-btn ${isEditMode ? 'is-save' : ''}`}
            onClick={isEditMode ? handleSubmit : () => setEditMode(true)}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="btn-spin" />
                Saving...
              </>
            ) : isEditMode ? (
              'Save Changes'
            ) : (
              'Edit User'
            )}
          </button>
        </div>
      </div>

      {/* User identity card */}
      <div className="detail-identity-card">
        <div className="identity-avatar">
          {userDetail.profilePic ? (
            <img src={userDetail.profilePic} alt={userDetail.userName} className="avatar-img" />
          ) : (
            (userDetail.userName || userDetail.email || '?').charAt(0).toUpperCase()
          )}
        </div>
        <div className="identity-info">
          <h2 className="identity-name">{userDetail.userName || '—'}</h2>
          <p className="identity-email">{userDetail.email}</p>
          <div className="identity-badges">
            <span className={`role-badge role-${userDetail.role}`}>{userDetail.role}</span>
            <span
              className={`status-badge ${userDetail.isActive ? 'status-active' : 'status-inactive'}`}
            >
              <span className="status-dot" />
              {userDetail.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        {/* Stat chips */}
        <div className="identity-stats">
          {PROGRESS_STAT_FIELDS.map(stat => (
            <div
              key={stat.key}
              className="stat-chip"
              style={{ '--stat-color': stat.color } as CSSProperties}
            >
              <span className="stat-value">{userProgress?.[stat.key] ?? '—'}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <Form noValidate onSubmit={handleSubmit}>
        {/* Section: Personal Details */}
        <div className="detail-section">
          <div className="section-header">
            <h4 className="section-title">Personal Details</h4>
          </div>
          <div className="fields-grid">
            {/* Username */}
            <div className="field-item">
              <label className="field-label">Username</label>
              {isEditMode ? (
                <input
                  className={`field-input ${errors.user?.userName ? 'input-error' : ''}`}
                  defaultValue={userDetail.userName}
                  onChange={e => setField('user', 'userName', e.target.value)}
                />
              ) : (
                <span className="field-value">{userDetail.userName || '—'}</span>
              )}
              {errors.user?.userName && <span className="error-msg">{errors.user.userName}</span>}
            </div>

            {/* Email */}
            <div className="field-item">
              <label className="field-label">Email</label>
              {isEditMode ? (
                <input
                  className={`field-input ${errors.user?.email ? 'input-error' : ''}`}
                  type="email"
                  defaultValue={userDetail.email}
                  onChange={e => setField('user', 'email', e.target.value)}
                />
              ) : (
                <span className="field-value">{userDetail.email || '—'}</span>
              )}
              {errors.user?.email && <span className="error-msg">{errors.user.email}</span>}
            </div>

            {/* Wallet ID */}
            <div className="field-item field-wide">
              <label className="field-label">Wallet ID</label>
              {isEditMode ? (
                <input
                  className="field-input font-mono"
                  defaultValue={userDetail.walletId}
                  onChange={e => setField('user', 'walletId', e.target.value)}
                />
              ) : (
                <span className="field-value font-mono small-text">
                  {userDetail.walletId || '—'}
                </span>
              )}
            </div>

            {/* Role */}
            <div className="field-item">
              <label className="field-label">Role</label>
              {isEditMode ? (
                <div className="radio-group">
                  {['admin', 'user'].map(r => (
                    <label key={r} className="radio-option">
                      <input
                        type="radio"
                        name="role"
                        defaultChecked={userDetail.role === r}
                        onChange={() => setField('user', 'role', r)}
                      />
                      <span className="radio-label">{r.charAt(0).toUpperCase() + r.slice(1)}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <span className={`role-badge role-${userDetail.role}`}>{userDetail.role}</span>
              )}
            </div>
          </div>
        </div>

        {/* Section: User Holdings */}
        <div className="detail-section">
          <div className="section-header">
            <h4 className="section-title">User Holdings</h4>
            {isEditMode && (
              <button
                type="button"
                className="section-action-btn"
                onClick={() => resetSeason(userDetail._id)}
              >
                Reset Season
              </button>
            )}
          </div>
          <div className="fields-grid">
            {PROGRESS_STAT_FIELDS.map(f => (
              <div key={f.key} className="field-item">
                <label className="field-label">{f.label}</label>
                {isEditMode ? (
                  <input
                    className={`field-input ${errors.progress?.[f.key] ? 'input-error' : ''}`}
                    type={f.type}
                    defaultValue={userProgress?.[f.key]}
                    onChange={e => setField('progress', f.key, e.target.value)}
                  />
                ) : (
                  <span className="field-value">
                    {(userProgress?.[f.key] as React.ReactNode) ?? '—'}
                  </span>
                )}
                {errors.progress?.[f.key] && (
                  <span className="error-msg">{errors.progress[f.key]}</span>
                )}
              </div>
            ))}

            {/* Debug Mode */}
            <div className="field-item">
              <label className="field-label">Debug Mode</label>
              <div className="toggle-wrap">
                <Form.Check
                  disabled={!isEditMode}
                  type="switch"
                  id="debugMode"
                  defaultChecked={userProgress?.debugMode}
                  onChange={e => setField('progress', 'debugMode', e.target.checked)}
                  className="custom-switch"
                />
              </div>
            </div>

            {/* World */}
            {!isEditMode && (
              <div className="field-item field-wide">
                <label className="field-label">World</label>
                <span className="field-value">{userProgress?.world?.world ?? '—'}</span>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <div className="section-header">
            <h4 className="section-title">
              Army
              {userArmy?.data && userArmy.data.length > 0 && (
                <span className="section-count">{userArmy.data.length}</span>
              )}
            </h4>
          </div>

          {isEditMode && (
            <div className="army-add-bar">
              <label className="field-label">Add by Hero ID(s)</label>
              <input
                className="field-input"
                placeholder="Comma-separated Hero IDs"
                onChange={e => setField('army', 'heroIds', e.target.value)}
              />
            </div>
          )}

          {userArmy?.data && userArmy.data.length > 0 ? (
            <div className="army-table-wrap">
              <table className="army-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {isEditMode
                      ? ['Hero ID', 'Name', 'In Use', 'Rarity', 'Hero Power', ''].map((h, i) => (
                          <th key={i}>{h}</th>
                        ))
                      : [
                          '',
                          'Hero ID',
                          'Name',
                          'In Use',
                          'Color',
                          'Piece Type',
                          'Rarity',
                          'Hero Power'
                        ].map((h, i) => <th key={i}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {userArmy.data.map((armyHero, index) => (
                    <tr key={index}>
                      <td className="col-num">{index + 1}</td>
                      {isEditMode ? (
                        <>
                          <td>{armyHero.hero?._id}</td>
                          <td>{armyHero.hero?.name}</td>
                          <td>
                            <Form.Check
                              inline
                              disabled
                              type="checkbox"
                              defaultChecked={armyHero.inUse}
                              className="m-auto"
                            />
                          </td>
                          <td>
                            <span
                              className={`rarity-badge rarity-${armyHero.hero?.rarity?.toLowerCase()}`}
                            >
                              {armyHero.hero?.rarity}
                            </span>
                          </td>
                          <td>{armyHero.hero?.heroPower || '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="act-btn act-destroy"
                              onClick={() => removeFromArmy(armyHero._id, index)}
                            >
                              Remove
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="col-avatar">
                            {armyHero.hero?.avatar ? (
                              <img
                                className="hero-img"
                                src={armyHero.hero.avatar}
                                alt={armyHero.hero?.name}
                              />
                            ) : (
                              <div className="hero-placeholder">♟</div>
                            )}
                          </td>
                          <td className="font-mono small-text">{armyHero.hero?._id}</td>
                          <td>{armyHero.hero?.name}</td>
                          <td>
                            <Form.Check
                              inline
                              disabled
                              type="checkbox"
                              checked={armyHero.inUse}
                              className="m-auto"
                            />
                          </td>
                          <td>
                            <span className={`color-badge color-${armyHero.hero?.color}`}>
                              {armyHero.hero?.color}
                            </span>
                          </td>
                          <td className="text-uppercase">{armyHero.hero?.pieceType}</td>
                          <td>
                            <span
                              className={`rarity-badge rarity-${armyHero.hero?.rarity?.toLowerCase()}`}
                            >
                              {armyHero.hero?.rarity}
                            </span>
                          </td>
                          <td>{armyHero.hero?.heroPower || '—'}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="army-empty">
              <span>♟</span>
              <p>No Heroes</p>
            </div>
          )}
        </div>
      </Form>
    </div>
  )
}

export default AdminUsersDetails
